import React, { useState, useEffect } from "react";
import { Eye, Pencil, Trash2, Plus } from "lucide-react";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const PageCrud = () => {
  const axiosInstance = useAxiosInstance();
  const [pages, setPages] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeStatus, setActiveStatus] = useState("");

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axiosInstance.get(`/api/public/page/getAll`);
      setPages(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await axiosInstance.delete(
        `/api/public/contacts/delete/${contactId}`);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting contact:", error);
    }
  };

  const openDeleteModal = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const openViewModal = (contact) => {
    setSelectedContact(contact);
    setShowViewModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setShowViewModal(false);
  };

  // View modal footer with close button
  const viewModalFooter = (
    <div className="flex justify-end">
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={closeModal}
      >
        Close
      </button>
    </div>
  );

  // Delete modal footer with action buttons
  const deleteModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
        onClick={() => handleDelete(selectedContact.contactId)}
      >
        Delete
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-800">Page List</h1>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Add Page</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-400">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Page Id</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Icon</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Component URL</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Display Order</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pages.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                  </tr>
                ) : (
                  pages.map((contact, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.pageid}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.pageicon}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.componenturl}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{contact.displayorder}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex justify-center gap-3">
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => openViewModal(contact)}
                          title="View Page"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          className="text-gray-500 hover:text-gray-700"
                          onClick={() => setActiveStatus(contact.activeStatus)}
                          title="Edit Page"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() => openDeleteModal(contact)}
                          title="Delete Page"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Modal using GlassmorphicModal */}
        <GlassmorphicModal
          isOpen={showViewModal}
          onClose={closeModal}
          title="Page Details"
          size="md"
          footer={viewModalFooter}
        >
          {selectedContact && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={selectedContact.name}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={selectedContact.name}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Component URL</label>
                <input
                  type="text"
                  value={selectedContact.componenturl}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <input
                  type="text"
                  value={selectedContact.planId}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                />
              </div>
            </div>
          )}
        </GlassmorphicModal>

        {/* Delete Modal using GlassmorphicModal */}
        <GlassmorphicModal
          isOpen={showDeleteModal}
          onClose={closeModal}
          title="Confirm Delete"
          size="sm"
          footer={deleteModalFooter}
        >
          {selectedContact && (
            <p className="text-gray-700">
              Are you sure you want to delete {selectedContact.name}? This action cannot be undone.
            </p>
          )}
        </GlassmorphicModal>
      </div>
    </div>
  );
};

export default PageCrud;